import React, { useContext, useEffect, useState, useRef } from "react";
import dayjs from "dayjs";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SendRoundedIcon from "@mui/icons-material/SendRounded";

import UserAvatar from "../management/UserAvatar";
import firebase from "../../firebase";
import ConversationMessage from "./ConversationMessage";
import ConversationGroupAvatar from "./ConversationGroupAvatar";
import ConfirmDeleteDialog from "../ConfirmDeleteDialog";
import { UserContext } from "../../context";
import { markConversationRead } from "./messageReadReceipts";

const AlwaysScrollToBottom = () => {
    const elementRef = useRef();

    useEffect(() => {
        elementRef.current?.scrollIntoView();
    });

    return <div ref={elementRef} />;
};

export default function ConversationDetail(props) {
    const [text, setText] = useState("");
    const [messages, setMessages] = useState([]);
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] =
        useState(false);
    const { currentUserData } = useContext(UserContext);
    const currentUserKey = currentUserData?.key;
    const [conversation, setConversation] = useState();
    const [canDelete, setCanDelete] = useState(false);
    const pendingReadReceipts = useRef(new Set());

    useEffect(() => {
        if (props.conversation) {
            if (props.conversation.deleted === false) {
                setConversation(props.conversation);
                setMessages(
                    props.conversation.messages.sort((a, b) =>
                        dayjs(a.date) > dayjs(b.date)
                            ? 1
                            : dayjs(b.date) > dayjs(a.date)
                              ? -1
                              : 0,
                    ),
                );

                markConversationRead(
                    props.conversation,
                    currentUserKey,
                    pendingReadReceipts.current,
                ).catch((error) => {
                    console.error(
                        "Unable to mark conversation as read",
                        error,
                    );
                });

                if (props.conversation.user && currentUserKey) {
                    if (props.conversation.user.key === currentUserKey) {
                        setCanDelete(true);
                    } else {
                        setCanDelete(false);
                    }
                }
            }
        }
    }, [props.conversation, currentUserKey]);

    function handleTextInput(event) {
        setText(event.target.value);
    }

    function handleReplyClick() {
        if (conversation && text !== "") {
            let messageRef = firebase
                .database()
                .ref("conversations/" + conversation.key + "/messages")
                .push();

            let message = {
                id: messageRef.key,
                message: text,
                date: dayjs().toString(),
                user: conversation.user.key,
                fullName: conversation.user.fullName,
                deleted: false,
            };

            messageRef.set(message);

            message.readBy = [];
            message.key = message.id;

            let conversationRef = firebase
                .database()
                .ref("conversations")
                .child(conversation.key);
            conversationRef.update({ lastMessage: dayjs().toString() });

            setText("");

            conversation.messages.push(message);
            setConversation(conversation);
        }
    }

    function handleCloseOnDeleteDialog(result) {
        if (result === true && currentUserData && conversation && canDelete) {
            let conversationRef = firebase
                .database()
                .ref("conversations")
                .child(conversation.key);

            conversationRef.update({ deleted: true });

            conversation.deleted = true;
            setConversation(undefined);
        }

        setConfirmDeleteDialogOpen(false);
    }

    function handleOpenOnDeleteDialog() {
        setConfirmDeleteDialogOpen(true);
    }

    if (!conversation) {
        return (
            <Box
                sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 4,
                    textAlign: "center",
                }}
            >
                <Box
                    sx={{
                        width: 60,
                        height: 60,
                        display: "grid",
                        placeItems: "center",
                        mb: 2,
                        borderRadius: 3,
                        color: "primary.main",
                        backgroundColor: "rgba(0, 149, 61, 0.08)",
                    }}
                >
                    <ChatBubbleOutlineIcon sx={{ fontSize: 30 }} />
                </Box>
                <Typography variant="h6">Choose a conversation</Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, maxWidth: 360 }}
                >
                    Select a conversation from the list to read and send
                    messages.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Box
                sx={{
                    minHeight: 76,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: { sm: 2.5, md: 3 },
                    py: 1.5,
                    flexShrink: 0,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                            ? "rgba(25, 32, 28, 0.94)"
                            : "rgba(255, 255, 255, 0.9)",
                }}
            >
                <Box sx={{ display: "flex", flexShrink: 0 }}>
                    {conversation.otherUsers.length === 1 ? (
                        <UserAvatar
                            userOrHub={conversation.otherUsers[0]}
                            path="users"
                            variant="circular"
                        />
                    ) : (
                        <ConversationGroupAvatar conversation={conversation} />
                    )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" component="h2" noWrap>
                        {conversation.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {conversation.otherUsers.length === 1
                            ? "Direct conversation"
                            : `${conversation.otherUsers.length + 1} participants`}
                    </Typography>
                </Box>
                {canDelete && (
                    <Tooltip title="Delete conversation">
                        <IconButton
                            aria-label="Delete conversation"
                            onClick={handleOpenOnDeleteDialog}
                            sx={{
                                color: "text.secondary",
                                "&:hover": {
                                    color: "error.main",
                                    backgroundColor:
                                        "rgba(211, 47, 47, 0.08)",
                                },
                            }}
                        >
                            <DeleteOutlineIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    scrollbarWidth: "thin",
                    backgroundImage: (theme) =>
                        theme.palette.mode === "dark"
                            ? "radial-gradient(circle at 15% 10%, rgba(31, 184, 94, 0.07), transparent 28%)"
                            : "radial-gradient(circle at 15% 10%, rgba(0, 149, 61, 0.045), transparent 28%)",
                }}
            >
                <List
                    disablePadding
                    sx={{
                        width: "100%",
                        maxWidth: 920,
                        mx: "auto",
                        px: { sm: 2.5, md: 4 },
                        py: 2.5,
                    }}
                >
                    {messages
                        .filter((message) => message.deleted === false)
                        .map((message) => (
                            <ConversationMessage
                                conversation={conversation}
                                message={message}
                                key={message.key}
                                hideMenu
                            />
                        ))}
                    <AlwaysScrollToBottom />
                </List>
            </Box>

            <Box
                sx={{
                    flexShrink: 0,
                    px: { sm: 2.5, md: 3 },
                    py: 1.5,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                }}
            >
                <Box
                    sx={{
                        maxWidth: 920,
                        mx: "auto",
                        display: "flex",
                        alignItems: "flex-end",
                        gap: 1.25,
                    }}
                >
                    <Box sx={{ display: "flex", mb: 0.5, flexShrink: 0 }}>
                        <UserAvatar
                            userOrHub={conversation.user}
                            size={props.avatarSize}
                            path="users"
                            variant="circular"
                        />
                    </Box>
                    <TextField
                        id="message-input"
                        placeholder="Write a message…"
                        multiline
                        maxRows={4}
                        value={text}
                        fullWidth
                        onChange={handleTextInput}
                        size="small"
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2.5,
                                backgroundColor: "background.subtle",
                            },
                        }}
                    />
                    <IconButton
                        aria-label="Send message"
                        onClick={handleReplyClick}
                        disabled={text === ""}
                        sx={{
                            width: 42,
                            height: 42,
                            mb: 0.1,
                            flexShrink: 0,
                            color: "common.white",
                            backgroundColor: "primary.main",
                            "&:hover": { backgroundColor: "primary.dark" },
                            "&.Mui-disabled": {
                                color: "action.disabled",
                                backgroundColor: "action.disabledBackground",
                            },
                        }}
                    >
                        <SendRoundedIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {confirmDeleteDialogOpen && (
                <ConfirmDeleteDialog
                    title="Delete conversation"
                    message="Are you sure you want to delete this conversation?"
                    detail={conversation.title}
                    open={confirmDeleteDialogOpen}
                    onClose={handleCloseOnDeleteDialog}
                />
            )}
        </Box>
    );
}
